import SelectField from "@imminently/decisively-ui/SelectField";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import { FieldWatch, useFormContext } from "@imminently/decisively-ui/Form";
import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { get } from "lodash";
import { AttributeCommunicationProxyForm } from "./AttributeCommunicationProxyForm";
import { AttributeIntegrationForm } from "./AttributeIntegrationForm";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GoalField } from "@/components/GoalField";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { useTemplateAttributes } from "@/features/release/hooks/useTemplateAttributes";
import { AttributeTemplateField } from "@/ui/forms/attribute-template-field";

interface CommunicationFieldsProps {
  scope: string;
  hideWho?: boolean;
  whoMessage?: string;
}

export const CommunicationFields = ({ scope, hideWho = false, whoMessage }: CommunicationFieldsProps) => {
  const { t } = useTranslation();
  const form = useFormContext<any>();
  const setFieldValue = (name: string, value: any) =>
    form.setValue(name, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const canEdit = useIsProjectTechnicalWriter();
  const graph = useParsedGraph();
  const templateAttributes = useTemplateAttributes(graph);

  const whoMethodOptions = [
    { value: "attribute", label: "Attribute (from rule base)" },
    { value: "integration", label: "Integration (API call)" },
  ];

  const scopedValue = (name: string) => `${scope}.${name}`;

  useEffect(() => {
    const currentWhoMethod = form.getValues(scopedValue("whoMethod"));
    if (currentWhoMethod === "attribute") {
      setFieldValue(scopedValue("whoIntegration"), undefined);
    }
  }, [form, scope]);

  return (
    <FieldWatch names={[scopedValue("method"), scopedValue("whoMethod")]}>
      {(values) => {
        const selectedMethod = get(values, scopedValue("method"));
        const isProxy = selectedMethod === "proxy";
        // Email bodies are HTML; SMS bodies (and every subject) are plain text.
        const isSms = selectedMethod === "sms";
        const whoMethod = get(values, scopedValue("whoMethod"), "attribute") as "attribute" | "integration";
        const isIntegrationWho = whoMethod === "integration";
        return (
          <>
            {isProxy && (
              <FormPanel>
                <FormPanel.Title>Proxy Settings</FormPanel.Title>
                <FormPanel>
                  <AttributeCommunicationProxyForm
                    scope={scope}
                    readOnly={!canEdit}
                  />
                </FormPanel>
              </FormPanel>
            )}

            {!hideWho && (
              <FormPanel>
                <FormPanel.Title>Recipient</FormPanel.Title>
                <FormPanel>
                  <SelectField
                    label="Who Method"
                    required
                    name={scopedValue("whoMethod")}
                    options={whoMethodOptions}
                    disabled={!canEdit}
                    fullWidth
                  />

                  {isIntegrationWho ? (
                    <AttributeIntegrationForm
                      scope={scopedValue("whoIntegration")}
                      showTitle={false}
                      showScheduling={false}
                      mappingType="assignment"
                    />
                  ) : (
                    <GoalField
                      required
                      label="Who"
                      name={scopedValue("who")}
                      disabled={!canEdit}
                    />
                  )}

                  <CheckboxField
                    label="Who is Contact ID (if checked, we'll fetch email/phone from contact record)"
                    name={scopedValue("whoIsContact")}
                    disabled={!canEdit}
                  />
                </FormPanel>
              </FormPanel>
            )}

            <FormPanel>
              <FormPanel.Title>{whoMessage || "Message"}</FormPanel.Title>
              <FormPanel>
                <AttributeTemplateField
                  label="Subject"
                  name={scopedValue("subject")}
                  required
                  disabled={!canEdit}
                  attributes={templateAttributes.attributes}
                  groupOrder={templateAttributes.groupOrder}
                  format="text"
                  singleLine
                  compact
                />
                {/* Keyed on the method so the body remounts in the right format when it changes. */}
                <AttributeTemplateField
                  key={`body-${isSms ? "sms" : "email"}`}
                  label="Body"
                  name={scopedValue("body")}
                  disabled={!canEdit}
                  attributes={templateAttributes.attributes}
                  groupOrder={templateAttributes.groupOrder}
                  format={isSms ? "text" : "html"}
                />
              </FormPanel>
            </FormPanel>
          </>
        );
      }}
    </FieldWatch>
  );
};
